import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CookieConsentService } from '../../../core/services/cookie-consent.service';

@Component({
  selector: 'app-cookie-consent-banner',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (cookieConsent.showBanner() || cookieConsent.showSettings()) {
      <!-- Backdrop -->
      @if (cookieConsent.showSettings()) {
        <div class="fixed inset-0 bg-black/50 z-[998] transition-opacity"
             (click)="closeSettings()"></div>
      }

      <div class="fixed bottom-0 inset-x-0 z-[999] animate-[slideUp_0.3s_ease-out]"
           role="dialog"
           aria-label="Setari cookie">
        <div class="mx-auto max-w-4xl px-4 pb-4">
          <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800
                      rounded-xl shadow-2xl p-5 sm:p-6">

            @if (!cookieConsent.showSettings()) {
              <!-- Simple banner -->
              <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <svg class="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                    <h2 class="text-base font-semibold text-gray-900 dark:text-white">
                      Acest site utilizeaza cookie-uri
                    </h2>
                  </div>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    Folosim cookie-uri pentru a imbunatati experienta de navigare si pentru analize statistice.
                    Puteti alege ce cookie-uri acceptati.
                    <a routerLink="/politica-cookie"
                       class="text-primary-600 dark:text-primary-400 hover:underline">
                      Aflati mai multe
                    </a>
                  </p>
                </div>
                <div class="flex flex-wrap gap-2 shrink-0">
                  <button (click)="cookieConsent.rejectAll()"
                          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                                 bg-gray-100 dark:bg-gray-800 rounded-lg
                                 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    Refuza tot
                  </button>
                  <button (click)="openCustomize()"
                          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                                 border border-gray-300 dark:border-gray-600 rounded-lg
                                 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    Personalizeaza
                  </button>
                  <button (click)="cookieConsent.acceptAll()"
                          class="px-4 py-2 text-sm font-medium text-white
                                 bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors">
                    Accepta tot
                  </button>
                </div>
              </div>
            } @else {
              <!-- Settings panel -->
              <div>
                <div class="flex items-center justify-between mb-5">
                  <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Setari cookie</h2>
                  <button (click)="closeSettings()"
                          class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg
                                 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          aria-label="Inchide">
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p class="text-sm text-gray-600 dark:text-gray-400 mb-5">
                  Alegeti ce categorii de cookie-uri doriti sa acceptati. Cookie-urile esentiale sunt necesare
                  pentru functionarea site-ului si nu pot fi dezactivate.
                </p>

                <div class="space-y-4">
                  <!-- Essential -->
                  <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div class="flex-1 min-w-0 mr-4">
                      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Cookie-uri esentiale</h3>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Necesare pentru functionarea site-ului. Includ preferinte de tema si sesiune.
                      </p>
                    </div>
                    <span class="text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30
                                 px-2.5 py-1 rounded-full shrink-0">
                      Intotdeauna activ
                    </span>
                  </div>

                  <!-- Statistics -->
                  <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div class="flex-1 min-w-0 mr-4">
                      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Cookie-uri statistice</h3>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Google Analytics si Microsoft Clarity — ne ajuta sa intelegem cum este utilizat site-ul.
                      </p>
                    </div>
                    <button role="switch"
                            [attr.aria-checked]="statisticsEnabled()"
                            (click)="statisticsEnabled.update(v => !v)"
                            class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
                                   transition-colors duration-200 ease-in-out focus-visible:outline-2 focus-visible:outline-offset-2
                                   focus-visible:outline-primary-600"
                            [class]="statisticsEnabled() ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'">
                      <span class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0
                                   transition duration-200 ease-in-out"
                            [class]="statisticsEnabled() ? 'translate-x-5' : 'translate-x-0'">
                      </span>
                    </button>
                  </div>

                  <!-- Marketing -->
                  <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div class="flex-1 min-w-0 mr-4">
                      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Cookie-uri de marketing</h3>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Utilizate pentru afisarea de reclame relevante (Google AdSense). Momentan inactive.
                      </p>
                    </div>
                    <button role="switch"
                            [attr.aria-checked]="marketingEnabled()"
                            (click)="marketingEnabled.update(v => !v)"
                            class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
                                   transition-colors duration-200 ease-in-out focus-visible:outline-2 focus-visible:outline-offset-2
                                   focus-visible:outline-primary-600"
                            [class]="marketingEnabled() ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'">
                      <span class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0
                                   transition duration-200 ease-in-out"
                            [class]="marketingEnabled() ? 'translate-x-5' : 'translate-x-0'">
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex flex-wrap justify-end gap-2 mt-5 pt-4 border-t border-gray-200 dark:border-gray-800">
                  <button (click)="cookieConsent.rejectAll()"
                          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                                 bg-gray-100 dark:bg-gray-800 rounded-lg
                                 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    Refuza tot
                  </button>
                  <button (click)="savePreferences()"
                          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                                 border border-gray-300 dark:border-gray-600 rounded-lg
                                 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    Salveaza preferintele
                  </button>
                  <button (click)="cookieConsent.acceptAll()"
                          class="px-4 py-2 text-sm font-medium text-white
                                 bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors">
                    Accepta tot
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    @keyframes slideUp {
      from { transform: translateY(100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `,
})
export class CookieConsentBannerComponent {
  protected readonly cookieConsent = inject(CookieConsentService);

  protected readonly statisticsEnabled = signal(true);
  protected readonly marketingEnabled = signal(false);

  protected openCustomize(): void {
    const current = this.cookieConsent.consent();
    if (current) {
      this.statisticsEnabled.set(current.statistics);
      this.marketingEnabled.set(current.marketing);
    }
    this.cookieConsent.openSettings();
  }

  protected closeSettings(): void {
    this.cookieConsent.showSettings.set(false);
    if (!this.cookieConsent.hasConsent()) {
      this.cookieConsent.showBanner.set(true);
    }
  }

  protected savePreferences(): void {
    this.cookieConsent.savePreferences({
      statistics: this.statisticsEnabled(),
      marketing: this.marketingEnabled(),
    });
  }
}
